/**
 * client/src/lib/use-invitation-push.ts
 *
 * Invitation WebSocket push listener (#2171) — the in-app half of telling
 * someone that an organisation, or another person, is waiting on their answer.
 *
 * Listens to BOTH `invitation:received` (the org rails) and the pre-existing
 * `guardian:invite_received` (a person asking to be protected), normalising the
 * two into one frame so callers handle one shape. Both are `broadcastToUser`
 * events addressed to this person; both are classified in
 * server/websocket/ws-event-policy.ts.
 *
 * Modelled directly on `use-member-lifecycle-push.ts` (Reuse-First Check,
 * DEVELOPMENT/quality.md Code Review Criteria #8): same `{type:"auth", userId,
 * sessionId}` handshake, because the server fan-out here is also
 * `broadcastToUser` (orgs/oversight/invitation-notify.ts), NOT the
 * `dashboard:<orgId>` channel-subscribe vocabulary.
 *
 * No `orgId` filter, unlike its sibling: this notice is addressed to the
 * person, not to a page rendering one org, and it must reach them whatever
 * context they happen to be in.
 *
 * REACH IS NOT GUARANTEED — a WebSocket notice reaches a LIVE session only, so
 * this is defence in depth over the nav badge (`pending-invitations.ts`), which
 * is a plain query and works for every account. No copy anywhere may present
 * this push as the delivery mechanism.
 *
 * NO liveness/deepfake claim: a notice of an already-committed, already-
 * attributed action, never proof anyone is a live human.
 */

import { titleCase } from "@/lib/text-format";
import { useCurrentUser } from "@/lib/use-current-user";
import { useReconnectingWs, type UseReconnectingWsResult } from "@/lib/use-reconnecting-ws";

export type InvitationKind = "org_membership" | "connection" | "guardian";

/**
 * Two DIFFERENT server events land here, so every field below the discriminator
 * is optional. `invitation:received` (orgs/oversight/invitation-notify.ts)
 * carries the org fields; `guardian:invite_received`
 * (logic/guardian/invite-delivery.ts) carries `fromDisplayName` and no org at
 * all. Typing the org fields as required would be a lie the first time a
 * guardian frame arrived.
 */
export interface InvitationFrame extends Record<string, unknown> {
  type: string;
  kind: InvitationKind;
  orgId?: string;
  orgName?: string;
  invitedByDisplayName?: string;
  role?: string;
  /** Guardian frames only — the person asking to be protected. */
  fromDisplayName?: string;
}

/**
 * The guardian event predates this listener and carries no `kind` of its own,
 * so its kind is derived from the event type rather than trusted off the wire;
 * the org events already carry theirs.
 */
function normaliseKind(frame: InvitationFrame): InvitationFrame {
  return frame.type === "guardian:invite_received" ? { ...frame, kind: "guardian" } : frame;
}

/**
 * Listens for `invitation:received` pushes addressed to the current
 * authenticated user. A null/loading current user opens no socket at all
 * (readiness-gate contract, same as `useCommPush`).
 *
 * RESIDUAL CLOSED (ledger #131, AC-96). The synthetic `sessionId` below used to
 * be what the server stored, so `closeSessionConnections` matched nothing and no
 * session termination ever closed this socket. The handshake now derives the
 * session id from the connection's own signed cookie and ignores this label
 * (server/websocket/setup.ts), so a session that ends takes its sockets with it.
 * The field is still sent because the server requires it in the handshake; it
 * names the listener, and nothing authorises off it.
 */
export function useInvitationPush(
  onEvent: (frame: InvitationFrame) => void,
): UseReconnectingWsResult {
  const { user } = useCurrentUser();

  return useReconnectingWs<InvitationFrame>(
    () =>
      user?.id
        ? { type: "auth", userId: user.id, sessionId: `invitation-push-${user.id}` }
        : null,
    (msg) => msg.type === "invitation:received" || msg.type === "guardian:invite_received",
    // Normalise before it reaches the caller, so no consumer has to know which
    // of the two server vocabularies produced the frame.
    (frame) => onEvent(normaliseKind(frame)),
    user?.id,
  );
}

/**
 * Plain-English, honest one-line summary. States only what happened — an
 * organisation asked — never that anything has taken effect, since nothing has
 * until the person accepts.
 *
 * The server sends "" rather than a fabricated placeholder when it cannot
 * resolve a name, so both fallbacks below are deliberate honest generics.
 */
export function describeInvitation(frame: InvitationFrame): string {
  if (frame.kind === "guardian") {
    // The guardian sender writes the literal string "Unknown" when it cannot
    // resolve a display name (logic/guardian/invite-delivery.ts), so it is
    // treated as the not-resolved case it is rather than rendered as a name.
    const from = frame.fromDisplayName;
    const who = !from || from === "Unknown" ? "Someone" : from;
    return `${who} asked you to be their guardian`;
  }
  const org = frame.orgName || "An organisation";
  if (frame.kind === "org_membership") {
    return frame.role
      ? `${org} invited you to join as ${titleCase(frame.role)}`
      : `${org} invited you to join`;
  }
  return `${org} would like to connect with you`;
}
